import { ReactElement, useEffect, useRef, useState } from 'react';
import * as S from './SideMenu.styles';
import { SideMenuProps } from './SideMenu.types';
import useOnClickOutside from '../../service/UseOutsideClick';
import { useThemeContext } from '@/common/app/contexts/ThemeContext';
import Link from 'next/link';
import { getLS, LocalStorageTypes } from '@/common/service/storage';
import { TCommonTypeActivityProps } from '@/shared/FeaturedActivities/domain/FeaturedActivities.domain';
import { Router, useRouter } from 'next/router';
import URL from '@/common/app/utils/urlsUtils';
import { useChatStore } from '@/entities/Chat/ui/useChatStore';

// import { Button } from '@/common/ui';
// import { Metamask } from './Metamask/Metamask';

export const SideMenu = ({ onChangeOpen, onTalkToUs }: SideMenuProps): ReactElement => {
    const { toggleTheme, theme } = useThemeContext();
    const isDark = theme.name === 'dark';
    const ref = useRef<HTMLDivElement>(null);
    const toggleChat = useChatStore((state) => state.toggleChat);

    const [touchStartX, setTouchStartX] = useState(0);
    const [touchStartY, setTouchStartY] = useState(0);
    const [recentViewed, setRecentViewed] = useState<TCommonTypeActivityProps[]>([]);
    const [hasExpiredItems, setHasExpiredItems] = useState(false);

    useEffect(() => {
        const json = getLS(LocalStorageTypes.LS_RECENTLY_VIEWED);

        if (json && Array.isArray(json)) {
            setRecentViewed(
                json.filter(
                    (activity) =>
                        activity &&
                        typeof activity === 'object' &&
                        typeof activity.name === 'string'
                )
            );
        }

        const expiredCart = getLS(LocalStorageTypes.LS_CART_EXPIRED);

        setHasExpiredItems(expiredCart && expiredCart?.items?.length);
    }, []);

    const { pathname } = useRouter();
    const notCheckoutPage = pathname !== '/cart';

    const hideMenu = () => {
        if (ref.current) {
            ref.current.style.transform = 'translateX(-100%)';
        }
    };

    const handleClickOutside = () => {
        hideMenu();

        setTimeout(() => {
            onChangeOpen(false);
        }, 300);
    };

    const scrollToRecent = () => {
        setTimeout(() => {
            window.scrollTo({
                top: 550,
                behavior: 'smooth',
            });
            Router.events.off('routeChangeComplete', scrollToRecent);
        }, 300);
    };

    const handleClickRecent = () => {
        hideMenu();

        setTimeout(() => {
            onChangeOpen(false);
            Router.events.on('routeChangeComplete', scrollToRecent);
        }, 300);
    };

    const openTalkToUs = () => {
        hideMenu();

        setTimeout(() => {
            onChangeOpen(false);
            onTalkToUs();
        }, 300);
    };

    useOnClickOutside(ref, handleClickOutside);

    const stoped = (e: React.TouchEvent<HTMLDivElement>) => {
        const diffX = touchStartX - e.changedTouches[0].clientX;
        const diffY = touchStartY - e.changedTouches[0].clientY;
        if (diffX > diffY && diffX > 40) {
            setTouchStartX(0);
            setTouchStartY(0);
            handleClickOutside();
        }
    };

    return (
        <S.StyleSideMenuWrapper
            ref={ref}
            onTouchStart={(e) => {
                setTouchStartX(e.changedTouches[0].clientX);
                setTouchStartY(e.changedTouches[0].clientY);
            }}
            onTouchEnd={stoped}
            data-test-id="Sidemenu"
        >
            <S.IconsWrapper>
                <Link href="/" passHref>
                    <a>
                        <S.Logotype />
                    </a>
                </Link>
                <div onClick={handleClickOutside}>
                    <S.ArrowButton />
                </div>
            </S.IconsWrapper>
            <S.LinksWrapper>
                <LinkItem name="Explore" href="/destination/" onClick={handleClickOutside} />
                {Boolean(recentViewed.length) && (
                    <LinkItem name="Recently Viewed" href="/" onClick={handleClickRecent} />
                )}
                {Boolean(hasExpiredItems) && (
                    <LinkItem
                        name="Expired Cart"
                        href="/expired-cart/"
                        onClick={handleClickOutside}
                    />
                )}
                <LinkItem name="Wish List" href="/wishlist/" onClick={handleClickOutside} />
                <LinkItem name="Landmark Guides" href="/landmarks/" onClick={handleClickOutside} />
                <LinkItem name="Blog" href="/blog/" onClick={handleClickOutside} />
                <LinkItem name="Tour Partners" href="/partners/" onClick={handleClickOutside} />
            </S.LinksWrapper>
            <S.LinksWrapper>
                <S.SwipeButton />
                <S.LocalSuportItems onClick={openTalkToUs}>
                    <S.TalkToUsIcon />
                    Talk to us
                </S.LocalSuportItems>
                <S.LocalSuportItems
                    onClick={() => {
                        handleClickOutside();
                        toggleChat(true);
                    }}
                >
                    <S.ChatIcon />
                    Chat with us
                </S.LocalSuportItems>
                <Link href="/customer-service-guide/" passHref>
                    <S.LocalSuportItems onClick={handleClickOutside}>
                        <S.HelpIcon />
                        Help
                    </S.LocalSuportItems>
                </Link>
            </S.LinksWrapper>
            {notCheckoutPage && (
                <S.ColorTheme onClick={toggleTheme}>
                    Dark mode:&nbsp;
                    {isDark ? 'on' : 'off'}&nbsp;
                    <S.Toggle isDark={isDark}>
                        {isDark ? <S.SwitchToLight /> : <S.SwitchToDark />}
                    </S.Toggle>
                </S.ColorTheme>
            )}
            <S.Bottom>
                {/* <Metamask />
                    <Button variant="primary" onClick={() => console.log('Connect MetaMask')}>
                        <Link href="https://metamask.io/download/" passHref>
                            Connect Wallet
                        </Link>
                    </Button> */}
                <Link href={`${URL.partners}/activity-operators/`} passHref>
                    <S.ListLink>List your tour</S.ListLink>
                </Link>
            </S.Bottom>
        </S.StyleSideMenuWrapper>
    );
};
const LinkItem = ({
    href,
    name,
    items,
    onClick,
}: {
    href: string;
    name: string;
    items?: number;
    onClick: () => void;
}): ReactElement => {
    return (
        <Link href={href} passHref>
            <S.LinkItem items={items} onClick={onClick}>
                {name}
            </S.LinkItem>
        </Link>
    );
};
